import React from 'react';
import { OFFER_SOURCE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import { GetOfferById_offer } from '@frontend/applications/AffiliatesApp/queries/types/GetOfferById';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faFilterCircleDollar } from '@fortawesome/free-solid-svg-icons';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@frontend/shadcn/components/ui/accordion';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2';
import { AutomatedPayoutLabel } from '@frontend/applications/AffiliatesApp/constants/offers';
import { TMember } from '../../../MembersWizard/types';
import { CustomColumn } from '../../RefreshManageOffer';

interface EditPayoutData {
    key: number;
    name: string;
    code: string;
    payoutId?: number;
    status: string;
    affiliateLink: string;
}

interface IProps {
    defaultPayoutId: number;
    payoutOptions: { id: number; label: string }[];
    setDefaultPayoutId: (id: number) => void;
    editPayoutData: EditPayoutData[];
    selectedMembers: readonly TMember[];
    offer: GetOfferById_offer;
    offerSource?: OFFER_SOURCE;
    columns: CustomColumn[];
    data: readonly TMember[];
}

const RefreshManageOfferPayout: React.FC<IProps> = ({
    defaultPayoutId,
    payoutOptions,
    setDefaultPayoutId,
    editPayoutData,
    selectedMembers,
    offer,
    offerSource,
    columns,
    data,
}) => (
  <div className="flex flex-col gap-4">
    <p className="text-sm font-medium text-grey-6 m-0">Update Payouts</p>
    <div className="bg-grey-0 border border-grey-2 rounded-2xl p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFilterCircleDollar} className="text-teal-4 w-4 h-4" />
          <p className="text-sm font-medium text-grey-6 m-0">Select Payouts</p>
        </div>

        <div>
          <FontAwesomeIcon icon={faCircleInfo} className="text-grey-4 w-4 h-4" />

        </div>
      </div>
      <div className="mb-4">
        <Select
          value={String(defaultPayoutId)}
          onValueChange={(id) => setDefaultPayoutId(Number(id))}
        >
          <SelectTrigger className="">
            <SelectValue placeholder="Select Payout" />
          </SelectTrigger>
          <SelectContent className="w-full">
            {payoutOptions.map((payout) => (
              <SelectItem value={String(payout.id)} key={payout.id}>
                <div className="flex items-center gap-2">
                  <span>{payout.label}</span>
                </div>
              </SelectItem>
                        ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm font-normal text-primary m-0">Note: Please ensure you want to upgrade/downgrade any member payout as this action will change the value for all selected members.</p>
    </div>
    <div className="space-y-2">
      <p className="text-xs font-medium text-grey-6 m-0">{data.length} {' '}{data.length === 1 ? 'Member' : 'Members'}{' '}have the following payouts for this offer</p>

      {/* Manual Payouts */}
      {offer.payouts
        .filter((payout) => !payout.offerAutomationConfigId) // Only manual payouts
        .map((payout, index) => {
          const filteredData = editPayoutData.filter((member) => member.payoutId === payout.id);
          if (!filteredData.length) return null;
          return (
            <Accordion type="single" collapsible className="w-full" key={`manual-${index}`}>
              <AccordionItem value={`manual-${index}`} className="!border-none">
                <AccordionTrigger className=" border border-grey-2 hover:no-underline hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5 px-3 py-2 rounded-xl m-0 justify-between">
                  <div className="w-3/4">
                    {offerSource === OFFER_SOURCE.SHOPIFY
                      && `${payout.label} (${payout.flatPayout ? `$${payout.flatPayout}` : ''}${payout.percentPayout ? `+${payout.percentPayout}%` : ''
                      })`}
                    {offerSource === OFFER_SOURCE.TUNE
                        && `${payout.label} (${payout.flatPayout ? `$${payout.flatPayout}` : ''}${payout.percentPayout ? ` + ${payout.percentPayout}%` : ''
                        })`}
                  </div>

                  <div className="flex items-center gap-1">
                    <p className="text-xs text-grey-5 font-normal m-0"> {`${filteredData.length}/${selectedMembers.length} ${selectedMembers.length === 1 ? 'member' : 'members'}`}</p>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <DataTable
                    columns={columns}
                    data={filteredData}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          );
        })}

      {/* Automated Payouts */}
      {(() => {
        // Get all automated payouts
        const automatedPayouts = offer.payouts.filter((payout) => payout.offerAutomationConfigId);

        if (automatedPayouts.length === 0) return null;

        // Get all members that belong to automated payouts or have null payoutId
        const automatedMembers = editPayoutData.filter((member) => {
          // Members with null payoutId (automated members)
          if (member.payoutId === null) return true;

          // Members assigned to automated payouts
          const assignedPayout = automatedPayouts.find((payout) => payout.id === member.payoutId);
          return assignedPayout && assignedPayout.offerAutomationConfigId;
        });

        if (automatedMembers.length === 0) return null;

        return (
          <Accordion type="single" collapsible className="w-full" key="automated">
            <AccordionItem value="automated" className="!border-none">
              <AccordionTrigger className=" border border-grey-2 hover:no-underline hover:bg-grey-1 hover:border-grey-5 hover:text-grey-5 px-3 py-2 rounded-xl m-0 justify-between">
                <div className="w-3/4">
                  <span className="font-medium">{AutomatedPayoutLabel}</span>
                </div>

                <div className="flex items-center gap-1">
                  <p className="text-xs text-grey-5 font-normal m-0"> {`${automatedMembers.length}/${selectedMembers.length} ${selectedMembers.length === 1 ? 'member' : 'members'}`}</p>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <DataTable
                  columns={columns}
                  data={automatedMembers}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
      })()}
    </div>
  </div>
);

export default RefreshManageOfferPayout;
